import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtRM } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Building2, Users, MessageSquare, Cpu, DollarSign, TrendingUp, Activity, Shield, X
} from "lucide-react";

const STATUS = {
  active: "bg-emerald-100 text-emerald-700", trial: "bg-sky-100 text-sky-700", past_due: "bg-rose-100 text-rose-700",
};
const HEALTH = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-rose-500" };

const KPI = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="h-10 w-10 rounded-xl bg-accent text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
    <p className="mt-3 text-2xl font-bold font-display">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export default function PlatformAdmin() {
  const [sel, setSel] = useState(null);
  const { data: o } = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data) });
  const { data: tenants = [] } = useQuery({ queryKey: ["admin-tenants"], queryFn: () => api.get("/admin/tenants").then((r) => r.data) });
  const { data: detail } = useQuery({
    queryKey: ["admin-tenant", sel], queryFn: () => api.get(`/admin/tenants/${sel}`).then((r) => r.data), enabled: !!sel,
  });

  if (!o) return <div className="p-6"><div className="h-8 w-40 bg-secondary rounded animate-pulse" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="platform-admin-page">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Platform Admin</h1>
          <p className="text-muted-foreground text-sm">Pandangan pemilik SaaS — semua tenant, penggunaan & hasil.</p>
        </div>
        <Badge className="bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-0 rounded-full ml-2">Super Admin</Badge>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Building2} label="Total Tenants" value={o.total_tenants} testid="admin-kpi-tenants" />
        <KPI icon={DollarSign} label="MRR" value={fmtRM(o.mrr)} testid="admin-kpi-mrr" />
        <KPI icon={TrendingUp} label="ARR" value={fmtRM(o.arr)} testid="admin-kpi-arr" />
        <KPI icon={Users} label="Total Users" value={o.total_users} testid="admin-kpi-users" />
        <KPI icon={MessageSquare} label="Total Messages" value={o.total_messages.toLocaleString()} testid="admin-kpi-messages" />
        <KPI icon={Cpu} label="AI Usage" value={o.total_ai_usage.toLocaleString()} testid="admin-kpi-ai" />
        <KPI icon={MessageSquare} label="WhatsApp Connections" value={o.total_whatsapp} testid="admin-kpi-wa" />
        <KPI icon={Activity} label="Active / Trial / Due" value={`${o.active}/${o.trial}/${o.past_due}`} testid="admin-kpi-status" />
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-semibold">Tenants ({tenants.length})</h2>
          <div className="flex gap-1.5">
            {o.plan_breakdown.map((p) => <Badge key={p.plan} variant="outline" className="rounded-full text-[10px]">{p.plan}: {p.count}</Badge>)}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Syarikat</th>
                <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Plan</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3 hidden sm:table-cell">Users</th>
                <th className="text-right font-semibold px-4 py-3 hidden lg:table-cell">Messages</th>
                <th className="text-right font-semibold px-4 py-3">MRR</th>
                <th className="text-center font-semibold px-4 py-3">Health</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`admin-tenant-row-${t.id}`} onClick={() => setSel(t.id)}
                  className="border-t border-border hover:bg-secondary/40 transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">{t.company[0]}</div>
                      <div>
                        <p className="font-semibold">{t.company}</p>
                        <p className="text-xs text-muted-foreground">{t.owner_email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell"><Badge variant="outline" className="rounded-full text-[10px]">{t.plan}</Badge></td>
                  <td className="px-4 py-3"><Badge className={`${STATUS[t.status]} border-0 rounded-full text-[10px]`}>{t.status}</Badge></td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell">{t.users}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell text-muted-foreground">{t.messages.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtRM(t.mrr)}</td>
                  <td className="px-4 py-3 text-center"><span className={`inline-block h-2.5 w-2.5 rounded-full ${HEALTH[t.health]}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="admin-tenant-detail" className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.company || "Tenant"}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge className={`${STATUS[detail.status]} border-0 rounded-full`}>{detail.status}</Badge>
                <Badge variant="outline" className="rounded-full">{detail.plan} · {fmtRM(detail.mrr)}/bln</Badge>
                <Badge variant="outline" className="rounded-full">{detail.users} users · {detail.whatsapp} WhatsApp</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Conversations", detail.dashboard.conversations], ["Hot Leads", detail.dashboard.hot_leads],
                  ["Contacts", detail.dashboard.contacts], ["Deals Won", detail.dashboard.deals_won]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-border p-3 text-center">
                    <p className="text-lg font-bold font-display">{v}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">{l}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-accent/50 p-4 flex items-center justify-between">
                <div><p className="text-xs text-muted-foreground">Revenue (tenant)</p><p className="font-bold text-primary">{fmtRM(detail.dashboard.revenue)}</p></div>
                <div className="text-right"><p className="text-xs text-muted-foreground">Conversion</p><p className="font-bold">{detail.dashboard.conversion_rate}%</p></div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Pipeline</p>
                <div className="space-y-1.5">
                  {Object.entries(detail.dashboard.pipeline).map(([st, n]) => {
                    const max = Math.max(...Object.values(detail.dashboard.pipeline), 1);
                    return (
                      <div key={st} className="flex items-center gap-2">
                        <span className="text-[10px] w-24 text-muted-foreground">{st.replace(/_/g, " ")}</span>
                        <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(n / max) * 100}%` }} /></div>
                        <span className="text-xs w-6 text-right">{n}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
